import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Reveal, Overline } from "../components/Reveal";
import { ArrowUpRight, MapPin } from "lucide-react";

const ServiceCard = ({ s, featured }) => {
  const summary = s.short || s.description || "";
  return (
    <Link
      to={`/services/${s.slug}`}
      data-testid={`service-card-${s.slug}`}
      className={`group relative block overflow-hidden border border-white/10 hover:border-gold/40 transition-colors ${featured ? "aspect-[16/10]" : "aspect-[4/5]"}`}
    >
      {s.image ? (
        <img
          src={s.image}
          alt={s.title}
          className="absolute inset-0 h-full w-full object-cover opacity-45 group-hover:opacity-75 group-hover:scale-105 transition-all duration-700"
        />
      ) : (
        // Elegant fallback for services still awaiting photography
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,0.15),transparent_60%),linear-gradient(to_bottom_right,#0d0d0d,#050505_60%,#1a1503)]">
          <div className="absolute inset-0 opacity-[0.06]" style={{
            backgroundImage:
              "radial-gradient(circle at 25% 30%, rgba(212,175,55,0.4) 0%, transparent 40%), radial-gradient(circle at 75% 70%, rgba(212,175,55,0.35) 0%, transparent 45%)",
          }} />
          <div className="absolute top-8 left-8">
            <div className="w-10 h-px bg-gold/40 mb-3" />
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold/60">GI Remodeling</p>
          </div>
          <div className="absolute right-8 bottom-24 md:bottom-32 font-serif text-[8rem] leading-none opacity-[0.05] select-none">
            {s.title.charAt(0)}
          </div>
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
      <div className="relative h-full flex flex-col justify-end p-6 md:p-8">
        {featured && (
          <span className="text-[10px] uppercase tracking-[0.25em] text-gold mb-2">Most Requested</span>
        )}
        <h3 className={`font-serif ${featured ? "text-4xl md:text-5xl" : "text-2xl"}`}>{s.title}</h3>
        {summary ? (
          <p className="mt-2 text-white/60 font-body text-sm max-w-md leading-relaxed line-clamp-2">{summary}</p>
        ) : (
          <p className="mt-2 text-white/40 font-body text-sm max-w-md leading-relaxed italic">Custom project — request a quote for details.</p>
        )}
        <span className="mt-5 inline-flex items-center gap-2 text-gold text-xs uppercase tracking-widest group-hover:gap-3 transition-all">
          See more <ArrowUpRight size={14} />
        </span>
      </div>
    </Link>
  );
};

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  useEffect(() => { api.get("/services").then((r) => setServices(r.data)).catch(() => {}); }, []);

  const featured = services.slice(0, 2);
  const rest = services.slice(2);

  return (
    <div>
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-36 md:pt-44 pb-16">
        <Reveal>
          <Overline>What We Do</Overline>
          <h1 className="mt-6 font-serif font-light text-5xl md:text-7xl leading-[0.95] max-w-4xl">Our <em className="gold-gradient-text">Services</em> in Virginia.</h1>
          <p className="mt-8 max-w-2xl text-white/60 font-body text-lg leading-relaxed">
            From full kitchen and bathroom remodels to flooring, painting, roofing and everything in between — one trusted team for your entire home. Click any service to see previous work, materials and custom designs.
          </p>
          <p className="mt-4 inline-flex items-center gap-2 text-white/50 text-sm uppercase tracking-widest"><MapPin size={15} className="text-gold" /> Serving all of Virginia</p>
        </Reveal>
      </section>

      {featured.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-6">
          <div className="grid md:grid-cols-2 gap-4">
            {featured.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.1}><ServiceCard s={s} featured /></Reveal>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-28">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rest.map((s, i) => (
            <Reveal key={s.id} delay={(i % 3) * 0.06}><ServiceCard s={s} /></Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-white/10 bg-[#080808] py-24 text-center">
        <Reveal>
          <h2 className="font-serif font-light text-4xl md:text-5xl px-6">Not sure where to start?</h2>
          <p className="mt-4 text-white/60 font-body">Tell us about your project and we’ll guide you every step of the way.</p>
          <Link data-testid="services-cta" to="/contact" className="mt-8 inline-flex items-center gap-3 bg-gold text-black text-sm font-semibold uppercase tracking-widest px-8 py-4 hover:bg-white transition-colors duration-300">
            Request a Free Quote <ArrowUpRight size={18} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
